import React from "react";

// http(s)://…, www.…, or bare domains with a TLD (e.g. neurips.cc/cfp).
const URL_RE =
  /https?:\/\/[^\s<>[\]"']+|www\.[^\s<>[\]"']+|\b[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])+)+\.[a-zA-Z]{2,}(?:\/[^\s<>[\]"']*)?/gi;

// dd-mm-yyyy style dates (dashes, slashes or dots).
const DATE_RE = /\b\d{1,2}[-/.]\d{1,2}[-/.]\d{4}\b/gi;

const MARKDOWN_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

// c{name1, name2} → collaborator tags. Must not follow a letter/digit, so words
// ending in "c" (e.g. "abc{...}") are left alone.
const COLLAB_RE = /(?<![A-Za-z0-9_])[cC]\{([^{}\n]*)\}/g;

const collaboratorNames = (raw) =>
  String(raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
const TRAILING_PUNCT_RE = /[.,;:!?)]+$/;

const stripTrailingPunct = (raw) =>
  String(raw || "").replace(TRAILING_PUNCT_RE, "");

const toHref = (raw) => {
  const url = stripTrailingPunct(raw).trim();
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const Link = ({ href, children, keyPrefix, index }) => (
  <a
    key={`${keyPrefix}-a${index}`}
    href={toHref(href)}
    target="_blank"
    rel="noopener noreferrer"
    className="text-cyan-400 hover:text-cyan-300 underline break-all"
    onClick={(e) => e.stopPropagation()}
  >
    {children}
  </a>
);

const linkifyPlain = (text, keyPrefix, keyStart = 0) => {
  const out = [];
  let lastIndex = 0;
  let i = keyStart;
  let match;
  const combined = new RegExp(`(${URL_RE.source})|(${DATE_RE.source})`, "gi");

  while ((match = combined.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      const display = stripTrailingPunct(match[1]);
      out.push(
        <Link
          key={`${keyPrefix}-a${i}`}
          keyPrefix={keyPrefix}
          index={i}
          href={match[1]}
        >
          {display}
        </Link>,
      );
    } else if (match[2]) {
      out.push(
        <span key={`${keyPrefix}-d${i}`} className="text-red-500 font-bold">
          {match[2]}
        </span>,
      );
    }

    i += 1;
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    out.push(text.slice(lastIndex));
  }

  return { nodes: out, nextIndex: i };
};

/**
 * Render a plain string as React nodes where:
 *  - URLs become clickable hyperlinks (open in a new tab),
 *  - [label](url) markdown links are supported, and
 *  - dd-mm-yyyy dates are highlighted in red to represent a deadline.
 *
 * With `{ collaborators: true }`, c{name1, name2} renders as one pink tag per
 * name (used for goal text).
 *
 * Returns the original string untouched when there's nothing to enrich.
 */
export function renderRichText(
  text,
  keyPrefix = "rt",
  { collaborators = false } = {},
) {
  if (text === null || text === undefined || text === "") return text;
  const str = String(text);
  if (collaborators && new RegExp(COLLAB_RE).test(str)) {
    return renderWithCollaborators(str, keyPrefix);
  }
  return renderLinksAndDates(str, keyPrefix);
}

function renderWithCollaborators(str, keyPrefix) {
  const out = [];
  const re = new RegExp(COLLAB_RE);
  let last = 0;
  let i = 0;
  let m;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) {
      out.push(
        <React.Fragment key={`${keyPrefix}-ct${i}`}>
          {renderLinksAndDates(str.slice(last, m.index), `${keyPrefix}-ct${i}`)}
        </React.Fragment>,
      );
    }
    const groupKey = `${keyPrefix}-c${i}`;
    collaboratorNames(m[1]).forEach((name, j) => {
      out.push(
        <span
          key={`${groupKey}-${j}`}
          className="inline-flex items-center gap-0.5 mx-0.5 px-1.5 py-px rounded-full bg-pink-500/15 text-pink-300 border border-pink-500/40 text-[0.85em] font-semibold align-baseline whitespace-nowrap"
          title="Collaborator"
        >
          <span aria-hidden="true">👥</span>
          {name}
        </span>,
      );
    });
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < str.length) {
    out.push(
      <React.Fragment key={`${keyPrefix}-ct${i}`}>
        {renderLinksAndDates(str.slice(last), `${keyPrefix}-ct${i}`)}
      </React.Fragment>,
    );
  }
  return out;
}

function renderLinksAndDates(str, keyPrefix) {
  if (!str) return str;

  const segments = [];
  let last = 0;
  let mdMatch;
  const mdRe = new RegExp(MARKDOWN_LINK_RE);
  while ((mdMatch = mdRe.exec(str)) !== null) {
    if (mdMatch.index > last) {
      segments.push({ type: "text", value: str.slice(last, mdMatch.index) });
    }
    segments.push({ type: "md", label: mdMatch[1], href: mdMatch[2] });
    last = mdMatch.index + mdMatch[0].length;
  }
  if (last < str.length) {
    segments.push({ type: "text", value: str.slice(last) });
  }
  if (segments.length === 0) {
    return str;
  }

  const out = [];
  let i = 0;
  for (const seg of segments) {
    if (seg.type === "md") {
      out.push(
        <Link
          key={`${keyPrefix}-a${i}`}
          keyPrefix={keyPrefix}
          index={i}
          href={seg.href}
        >
          {seg.label}
        </Link>,
      );
      i += 1;
    } else {
      const { nodes, nextIndex } = linkifyPlain(seg.value, keyPrefix, i);
      out.push(...nodes);
      i = nextIndex;
    }
  }

  return out.length ? out : str;
}
